/**
 * @file useSubscriptions.tsx
 *
 * @fileoverview Exports current user subscriptions hook.
 */

import { ApolloQueryResult, useQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { ContentErrorList } from '~/jobiqo/components/ContentErrorList';
import GET_SUBSCRIPTIONS from '~/jobiqo/components/Subscriptions/graphql/queries/getSubscriptions.gql';
import { QueryFilter } from '~/jobiqo/models/search';
import { EntityQuerySorting, Subscription } from '~/jobiqo/types/types';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';
import { useConfig } from '~/jobiqo/context/config';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

interface UseSubscriptionsProps {
  /**
   * The skip query flag.
   *
   * @default false
   */
  skip?: boolean;
  /**
   * The query variables.
   *
   * @default { filters: [], limit: 100 }
   */
  variables?: {
    filters: QueryFilter[];
    limit: number;
    offset?: number;
    sorting?: EntityQuerySorting[];
    showSubscriptionsFromAllFrontends?: boolean;
    organizationId?: number;
  };
}

export interface UseSubscriptionsInterface {
  /**
   * The loading flag.
   */
  loading: boolean;
  /**
   * The Error component.
   */
  Error: React.ReactNode;
  /**
   * The available subscriptions.
   */
  data: Subscription[];
  /**
   * The total number of subscriptions.
   */
  total: number;
  /**
   * The apollo refetch function to get subscriptions.
   */
  refetch?: () => Promise<ApolloQueryResult<any>>;
}

export default function useSubscriptions({
  skip = false,
  variables,
}: UseSubscriptionsProps): UseSubscriptionsInterface {
  const { ORGANIZATIONS } = useFrontendConfig();
  const SHOW_SUBSCRIPTIONS_FROM_ALL_FRONTENDS =
    ORGANIZATIONS.SHOW_SUBSCRIPTIONS_FROM_ALL_FRONTENDS;
  const { getConfigByName } = useConfig();
  const resolvedVariables = variables ?? {
    filters: [],
    limit: 100,
    showSubscriptionsFromAllFrontends: SHOW_SUBSCRIPTIONS_FROM_ALL_FRONTENDS,
    organizationId: undefined,
  };
  const SUBSCRIPTIONS_FEATURE_ACTIVE: boolean = !!getConfigByName(
    'jobiqo_subscription',
  )?.value[0];
  // Check if the subscription feature is active.
  if (!SUBSCRIPTIONS_FEATURE_ACTIVE) {
    return {
      data: [],
      loading: false,
      Error: null,
      total: 0,
    };
  }

  let Error: React.ReactNode = null;

  const { data, loading, error, refetch } = useQuery(GET_SUBSCRIPTIONS, {
    variables: resolvedVariables,
    skip,
    fetchPolicy: 'no-cache',
    // Needed because refetch does not update loading state.
    notifyOnNetworkStatusChange: true,
  });

  if (error) {
    Error = (
      <MessageAlert alertStyle="danger" type="assertive">
        <FormattedMessage
          id="contact_request_resume_flatrate_error"
          defaultMessage="An error happened while retrieving your subscription data."
        />
      </MessageAlert>
    );
  }

  if (
    data?.getSubscriptions?.errors &&
    data.getSubscriptions.errors.length > 0
  ) {
    Error = <ContentErrorList errors={data.getSubscriptions.errors} />;
  }

  return {
    data: data?.getSubscriptions?.subscriptions ?? [],
    loading,
    Error,
    total: data?.getSubscriptions?.total ?? 0,
    refetch,
  };
}
